import {
  BER_THONG_DEFAULT_VISUAL_THEME_ID,
  getBerThongVisualTheme,
} from './visualThemes'
import './berThongPoster.css'

export type BerThongPosterSlot = {
  id?: string
  number: string
  status: 'available' | 'sold'
  isWinner?: boolean
}

export type BerThongPosterProps = {
  themeId?: string | null
  title?: string
  pricePerSlot?: number
  prizeLabel?: string
  drawDate?: string
  closeLabel?: string
  huayName?: string
  sheetCode?: string
  slots: BerThongPosterSlot[]
  selectedNumbers?: string[]
  interactive?: boolean
  /** ปิดรับแทงแล้ว — แสดงแถบคาดทับ + ปิดการกดเบอร์ */
  bettingClosed?: boolean
  showThemeChip?: boolean
  footerNote?: string
  onToggleNumber?: (number: string, available: boolean) => void
  className?: string
}

function prizeWithHighlight(label: string) {
  const match = label.match(/^(.*?)(\d[\d,]*)(.*)$/)
  if (!match) return label
  return (
    <>
      {match[1]}
      <strong>{match[2]}</strong>
      {match[3]}
    </>
  )
}

export function BerThongPoster({
  themeId = BER_THONG_DEFAULT_VISUAL_THEME_ID,
  title = 'เบอร์เงินสด',
  pricePerSlot = 20,
  prizeLabel = '2 ตัวล่าง รับ 1300',
  drawDate = '',
  closeLabel = '',
  huayName = '',
  sheetCode = '',
  slots,
  selectedNumbers = [],
  interactive = false,
  bettingClosed = false,
  showThemeChip = false,
  footerNote = 'จ่ายเงินก่อนวันหวยออก ไม่จ่ายถือว่าสละสิทธิ์',
  onToggleNumber,
  className = '',
}: BerThongPosterProps) {
  const theme = getBerThongVisualTheme(themeId)
  const canInteract = interactive && !bettingClosed

  return (
    <article
      className={[
        'bt-poster',
        `bt-poster--${theme.id}`,
        bettingClosed ? 'is-betting-closed' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-theme={theme.id}
      aria-disabled={bettingClosed || undefined}
    >
      {showThemeChip ? <span className="bt-poster__theme-chip">{theme.name}</span> : null}
      {bettingClosed ? (
        <div className="bt-poster__closed-overlay" aria-hidden>
          <span className="bt-poster__closed-slash bt-poster__closed-slash--a" />
          <span className="bt-poster__closed-slash bt-poster__closed-slash--b" />
          <span className="bt-poster__closed-banner">ปิดรับแทง</span>
        </div>
      ) : null}
      <div className="bt-poster__inner">
        {(sheetCode || huayName) ? (
          <div className="bt-poster__meta">
            {sheetCode ? <span className="bt-poster__code">{sheetCode}</span> : null}
            {huayName ? <span className="bt-poster__huay">{huayName}</span> : null}
          </div>
        ) : null}
        <div className="bt-poster__top">
          <div className="bt-poster__badge" aria-label={`เบอร์ละ ${pricePerSlot}`}>
            <span className="bt-poster__badge-label">เบอร์ละ</span>
            <span className="bt-poster__badge-price">{pricePerSlot}</span>
          </div>

          <div className="bt-poster__headline">
            <h3 className="bt-poster__title">{title}</h3>
            <p className="bt-poster__prize">{prizeWithHighlight(prizeLabel)}</p>
            <div className="bt-poster__draw">
              <span>งวดวันที่</span>
              <span className="bt-poster__draw-pill">{drawDate || '—'}</span>
            </div>
            {closeLabel ? (
              <div className="bt-poster__close">
                <span>ปิดรับ</span>
                <span className="bt-poster__close-pill">{closeLabel}</span>
              </div>
            ) : null}
          </div>

          <div className="bt-poster__mascot" aria-hidden>
            <div className="bt-poster__mascot-face">
              <span className="bt-poster__mascot-smile" />
            </div>
          </div>
        </div>

        <div className="bt-poster__panel">
          <div className="bt-poster__grid" role="grid" aria-label="กริดเบอร์ 00-99">
            {slots.map((slot) => {
              const available = slot.status === 'available'
              const selected = selectedNumbers.includes(slot.number)
              return (
                <button
                  key={slot.id || slot.number}
                  type="button"
                  disabled={!canInteract || !available}
                  className={[
                    'bt-poster__cell',
                    canInteract ? 'is-interactive' : '',
                    available ? 'is-available' : 'is-sold',
                    selected ? 'is-selected' : '',
                    slot.isWinner ? 'is-winner' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    if (!canInteract || !available) return
                    onToggleNumber?.(slot.number, available)
                  }}
                >
                  {slot.number}
                </button>
              )
            })}
          </div>
        </div>

        {footerNote ? <p className="bt-poster__footer">{footerNote}</p> : null}
      </div>
    </article>
  )
}

export default BerThongPoster
